/**
 * Assets: files of any type kept with the work (pictures, texts, sounds...), as the core's
 * `assets` module keeps them (`assets.*`):
 *
 * | Source | Where | Who may change it |
 * |---|---|---|
 * | `project` | beside the project's file (a space's: on its server) | editors |
 * | `computer` | this computer's library, for every project (not in a space) | whoever uses it |
 *
 * The service mirrors both listings (read again on `assets.changed` and on every connection),
 * uploads files (at most {@link MAX_ASSET_BYTES} each), moves, removes, copies, downloads, and
 * gives pictures as addresses the page can show ({@link AssetsService.url}: read once, kept
 * until the asset changes). It also holds where the Assets drawer is and the asset previewed.
 */
import { batch, createSignal, type Accessor } from "solid-js";
import type { AssetInfo } from "@protocol/AssetInfo";
import type { AssetListing } from "@protocol/AssetListing";
import type { AssetSource } from "@protocol/AssetSource";
import { isClientError, type Client } from "../client/client";
import type { AccessService } from "./access";
import { assetName, joinPath, MAX_ASSET_BYTES, formatSize, parentOf } from "../shared/assets";
import { fromBase64, toBase64 } from "../shared/base64";
import { downloadBytes } from "../shared/download";
import { toast } from "../shared/toast";

/** One source as the drawer shows it. */
export interface AssetSourceView {
  /** Which one. */
  id: AssetSource;
  /** Its name ("Project files", "This computer's files"). */
  label: string;
  /** Whether it can be changed here; {@link AssetSourceView.why} says why not. */
  readOnly: boolean;
  /** Why it is read-only. */
  why: string;
  /** Its folders and files. */
  listing: AssetListing;
}

/** Where the drawer's files are browsed: a source and a folder (`""`: its top). */
export interface AssetPlace {
  /** The source. */
  source: AssetSource;
  /** The folder. */
  folder: string;
}

/** An asset read: what it is and its bytes. */
export interface AssetContent {
  /** What it is. */
  info: AssetInfo;
  /** Its bytes. */
  bytes: Uint8Array;
}

/** The assets and what can be done with them. */
export interface AssetsService {
  /** The sources shown, project first (this computer's only where the core has a library). */
  sources: Accessor<AssetSourceView[]>;
  /** One source (`undefined` when it is not shown here). */
  source(id: AssetSource): AssetSourceView | undefined;
  /** The project's asset at `path`, if any. */
  info(path: string): AssetInfo | undefined;
  /** Where the drawer browses files (`null`: it shows presets). */
  place: Accessor<AssetPlace | null>;
  /** Browses a folder of a source (`null`: back to presets). */
  go(place: AssetPlace | null): void;
  /** The asset shown big (`null`: none). */
  previewed: Accessor<AssetPlace & { path: string } | null>;
  /** Shows an asset big (`null` closes it). */
  preview(asset: { source: AssetSource; path: string } | null): void;
  /** Keeps the computer's `files` in `folder` of `source` (a taken name gets a number). Gives what was kept. */
  upload(source: AssetSource, files: Iterable<File>, folder: string): Promise<AssetInfo[]>;
  /** Makes folder `path`. */
  makeFolder(source: AssetSource, path: string): Promise<boolean>;
  /** Moves or renames an asset or folder (into `to` when it is a folder). */
  move(source: AssetSource, from: string, to: string): Promise<boolean>;
  /** Removes an asset or a folder with what is inside. */
  remove(source: AssetSource, path: string): Promise<boolean>;
  /** Copies an asset into a folder of another source. Gives the copy. */
  copy(from: AssetSource, path: string, to: AssetSource, folder: string): Promise<AssetInfo | null>;
  /** Reads an asset (`null`: not there, said in a toast unless `quiet`). */
  read(source: AssetSource, path: string, quiet?: boolean): Promise<AssetContent | null>;
  /** Saves an asset as a file of the computer (the browser's download). */
  download(source: AssetSource, path: string): Promise<void>;
  /**
   * An address the page can show for a picture (`blob:`), read in the background the first
   * time: `null` until then, or when it is not there. Reactive.
   */
  url(path: string, source?: AssetSource): string | null;
}

/** What {@link createAssets} needs. */
export interface AssetsDeps {
  /** The core this page talks to. */
  client: Client;
  /** Whether this page may change the project's assets (editors). */
  access: Pick<AccessService, "can">;
}

/** An empty listing of `source`. */
const empty = (source: AssetSource): AssetListing => ({ source, folders: [], files: [] });

/** Starts mirroring the assets. Call inside a Solid root. */
export function createAssets({ client, access }: AssetsDeps): AssetsService {
  const [project, setProject] = createSignal<AssetListing>(empty("project"));
  const [computer, setComputer] = createSignal<AssetListing | null>(null);
  const [place, setPlace] = createSignal<AssetPlace | null>(null);
  const [previewed, setPreviewed] = createSignal<(AssetPlace & { path: string }) | null>(null);
  /** Pictures read: key → address; goes up to tell the readers. */
  const urls = new Map<string, string | null>();
  const [urlsVersion, setUrlsVersion] = createSignal(0);

  /** Reads one source's listing again (quietly: a core with no library answers no). */
  const load = async (source: AssetSource) => {
    try {
      const response = await client.request({ method: "assets.list", params: { source } });
      if (response.type !== "assets") return;
      if (source === "project") setProject(response.listing);
      else setComputer(response.listing);
    } catch {
      if (source === "computer") setComputer(null);
    }
  };
  client.onOpen(() => void (load("project"), load("computer")));
  client.onEvent((event) => {
    if (event.event === "assets.changed") void load(event.data.source);
  });

  /** Sends a request, a refusal said in a toast; gives the answer or `null`. */
  const send = async (request: Parameters<Client["request"]>[0]) => {
    try {
      return await client.request(request);
    } catch (error) {
      toast(isClientError(error) ? error.message : String(error), "error");
      return null;
    }
  };

  const sources = (): AssetSourceView[] => {
    const editor = access.can("editor");
    const list: AssetSourceView[] = [{ id: "project", label: "Project files", readOnly: !editor, why: editor ? "" : "Editors and owners change the project's files.", listing: project() }];
    const library = computer();
    if (library) list.push({ id: "computer", label: "This computer's files", readOnly: false, why: "", listing: library });
    return list;
  };
  const source = (id: AssetSource) => sources().find((s) => s.id === id);
  /** Keeps a listing the core answered. */
  const keep = (id: AssetSource, listing: AssetListing) => (id === "project" ? setProject(listing) : setComputer(listing));
  const listingOf = (id: AssetSource) => (id === "project" ? project() : computer());

  /** A picture's key: its source, path and last change (a changed asset is read again). */
  const keyOf = (source: AssetSource, path: string) => {
    const info = listingOf(source)?.files.find((f) => f.path === path);
    return info ? `${source}:${path}:${info.modified_ms}:${info.size}` : null;
  };

  async function read(source: AssetSource, path: string, quiet = false): Promise<AssetContent | null> {
    try {
      const response = await client.request({ method: "assets.read", params: { source, path } });
      return response.type === "asset" ? { info: response.info, bytes: fromBase64(response.base64) } : null;
    } catch (error) {
      if (!quiet) toast(isClientError(error) ? error.message : String(error), "error");
      return null;
    }
  }

  return {
    sources,
    source,
    info: (path) => project().files.find((f) => f.path === path),
    place: () => {
      const p = place();
      return p && source(p.source) ? p : null;
    },
    go: setPlace,
    previewed,
    preview: (asset) => setPreviewed(asset ? { ...asset, folder: "" } : null),
    async upload(target, files, folder) {
      const kept: AssetInfo[] = [];
      for (const file of files) {
        if (file.size > MAX_ASSET_BYTES) {
          toast(`${file.name} is ${formatSize(file.size)}: an asset can be at most ${formatSize(MAX_ASSET_BYTES)}.`, "error");
          continue;
        }
        const base64 = toBase64(new Uint8Array(await file.arrayBuffer()));
        const response = await send({ method: "assets.write", params: { source: target, path: joinPath(folder, file.name), base64, replace: false } });
        if (response?.type === "asset") kept.push(response.info);
      }
      if (kept.length) void load(target);
      return kept;
    },
    async makeFolder(target, path) {
      const response = await send({ method: "assets.folder", params: { source: target, path } });
      if (response?.type === "assets") keep(target, response.listing);
      return response?.type === "assets";
    },
    async move(target, from, to) {
      if (from === to) return true;
      const response = await send({ method: "assets.move", params: { source: target, from, to } });
      if (response?.type === "assets") keep(target, response.listing);
      return response?.type === "assets";
    },
    async remove(target, path) {
      const response = await send({ method: "assets.remove", params: { source: target, path } });
      if (response?.type !== "assets") return false;
      batch(() => {
        keep(target, response.listing);
        const shown = previewed();
        if (shown && shown.source === target && (shown.path === path || shown.path.startsWith(`${path}/`))) setPreviewed(null);
      });
      return true;
    },
    async copy(from, path, to, folder) {
      const response = await send({ method: "assets.copy", params: { from, path, to, folder } });
      if (response?.type !== "asset") return null;
      void load(to);
      toast(`Copied ${assetName(path)} to ${source(to)?.label ?? to}${folder ? ` / ${folder}` : ""}.`);
      return response.info;
    },
    read,
    async download(target, path) {
      const content = await read(target, path);
      if (content) downloadBytes(assetName(path), content.bytes, content.info.mime);
    },
    url(path, target = "project") {
      void urlsVersion();
      const key = keyOf(target, path);
      if (!key) return null;
      if (urls.has(key)) return urls.get(key) ?? null;
      urls.set(key, null);
      void read(target, path, true).then((content) => {
        urls.set(key, content ? URL.createObjectURL(new Blob([content.bytes as BlobPart], { type: content.info.mime })) : null);
        setUrlsVersion((v) => v + 1);
      });
      return null;
    },
  };
}

/** What a page shows of `path`: whether the project has it, and a picture's address. */
export function fileShown(assets: Pick<AssetsService, "info" | "url">, path: string): { found: boolean; image: string | null } {
  const info = assets.info(path);
  return { found: !!info, image: info?.kind === "image" ? assets.url(path) : null };
}

/** Shows one of the project's files big, in the Assets drawer (opened for it). */
export function openFile(assets: Pick<AssetsService, "info" | "go" | "preview">, drawer: { setDrawer(open: boolean): void }, path: string): void {
  if (!assets.info(path)) return void toast(`There is no file ${path} in the project.`, "error");
  assets.go({ source: "project", folder: parentOf(path) });
  assets.preview({ source: "project", path });
  drawer.setDrawer(true);
}
